import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import LanguageToggle from '../LanguageToggle';
import { Lock, Mail, ArrowRight, Monitor, Eye, EyeOff, ArrowLeft, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientLogin() {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState(null);
  const [view, setView] = useState('login'); // login | forgot | reset | 2fa
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [twoFaCode, setTwoFaCode] = useState('');
  const [twoFaSlug, setTwoFaSlug] = useState('');
  const navigate = useNavigate();
  const { slug } = useParams();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (slug) {
      fetch(`${API}/api/client/branding/${slug}`)
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) setBranding(d); })
        .catch(() => {});
    }
    const token = searchParams.get('token');
    const resetEmail = searchParams.get('email');
    if (token && resetEmail) {
      setView('reset');
      setEmail(resetEmail);
    }
  }, [slug, searchParams]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/client/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || t('errLoginFail'));

      if (data.requires_2fa) {
        setTwoFaSlug(data.temp_slug);
        setView('2fa');
        setLoading(false);
        return;
      }

      localStorage.setItem('client_token', data.access_token);
      localStorage.setItem('client_data', JSON.stringify(data.client));
      navigate('/client/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/client/verify-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: twoFaSlug, code: twoFaCode, email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || t('errVerifyFail'));
      localStorage.setItem('client_token', data.access_token);
      localStorage.setItem('client_data', JSON.stringify(data.client));
      navigate('/client/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/client/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || t('errReqFail'));
      setSuccess(data.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/client/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token: searchParams.get('token'), new_password: newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || t('errResetFail'));
      setSuccess(data.message);
      setTimeout(() => { setView('login'); setSuccess(''); }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const brandColor = branding?.primary_color || '#2563eb';
  const brandName = branding?.company_name || t('clientPortal');
  const logoUrl = branding?.logo_url;

  return (
    <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center px-4" data-testid="client-login-page">
      <div className="w-full max-w-md">
        <div className="absolute top-4 right-4 z-10">
          <LanguageToggle variant="dark" />
        </div>
        <div className="text-center mb-8">
          {logoUrl ? (
            <img src={logoUrl} alt={brandName} className="h-14 mx-auto mb-4 object-contain" />
          ) : (
            <div className="flex items-center justify-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: brandColor }}>
                <Monitor className="h-4.5 w-4.5 text-white" />
              </div>
            </div>
          )}
          <h1 className="text-3xl font-bold text-white tracking-tight">{brandName}</h1>
          <p className="text-gray-400 mt-2 text-sm">
            {view === 'forgot' ? t('subForgot') :
             view === 'reset' ? t('subReset') :
             view === '2fa' ? t('sub2fa') :
             t('subSignIn')}
          </p>
        </div>

        <Card className="bg-[#111827] border-gray-800">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              {view !== 'login' && view !== '2fa' && (
                <button onClick={() => { setView('login'); setError(''); setSuccess(''); }} className="text-gray-400 hover:text-white" aria-label={t('backToSignIn')} title={t('backToSignIn')} data-testid="back-to-login-arrow">
                  <ArrowLeft className="h-4 w-4" />
                </button>
              )}
              {view === '2fa' && <ShieldCheck className="h-5 w-5 text-blue-400" />}
              {view === 'forgot' ? t('titleForgot') : view === 'reset' ? t('titleReset') : view === '2fa' ? t('title2fa') : t('titleSignIn')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* === LOGIN === */}
            {view === 'login' && (
              <form onSubmit={handleLogin} className="space-y-4">
                {error && <div className="bg-red-900/30 border border-red-700 text-red-300 px-3 py-2 rounded text-sm" data-testid="login-error">{error}</div>}
                {success && <div className="bg-green-900/30 border border-green-700 text-green-300 px-3 py-2 rounded text-sm">{success}</div>}
                <div className="space-y-2">
                  <Label className="text-gray-300 text-sm">{t('email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('emailPlaceholder')}
                      className="pl-10 bg-[#1a2236] border-gray-700 text-white placeholder:text-gray-500"
                      required data-testid="login-email" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300 text-sm">{t('password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
                    <Input type={showPassword ? 'text' : 'password'} value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('passwordPlaceholder')}
                      className="pl-10 pr-10 bg-[#1a2236] border-gray-700 text-white placeholder:text-gray-500"
                      required data-testid="login-password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-300"
                      data-testid="toggle-password-visibility">
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="flex justify-end">
                  <button type="button" onClick={() => { setView('forgot'); setError(''); setSuccess(''); }}
                    className="text-sm text-blue-400 hover:text-blue-300" data-testid="forgot-password-link">
                    {t('forgotPassword')}
                  </button>
                </div>
                <Button type="submit" disabled={loading} className="w-full text-white"
                  style={{ backgroundColor: brandColor }} data-testid="login-submit-btn">
                  {loading ? t('btnSigningIn') : t('btnSignIn')} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </form>
            )}

            {/* === 2FA VERIFICATION === */}
            {view === '2fa' && (
              <form onSubmit={handleVerify2FA} className="space-y-4">
                {error && <div className="bg-red-900/30 border border-red-700 text-red-300 px-3 py-2 rounded text-sm">{error}</div>}
                <p className="text-gray-400 text-sm">{t('twoFaIntro')}</p>
                <div className="space-y-2">
                  <Label className="text-gray-300 text-sm">{t('twoFaLabel')}</Label>
                  <div className="relative">
                    <ShieldCheck className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
                    <Input type="text" value={twoFaCode} onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000" maxLength={10}
                      className="pl-10 bg-[#1a2236] border-gray-700 text-white text-center text-xl tracking-[0.3em] placeholder:text-gray-600 font-mono"
                      autoFocus required data-testid="2fa-code-input" />
                  </div>
                </div>
                <Button type="submit" disabled={loading || twoFaCode.length < 6} className="w-full text-white"
                  style={{ backgroundColor: brandColor }} data-testid="2fa-verify-btn">
                  {loading ? t('btnVerifying') : t('btnVerify')} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <button type="button" onClick={() => { setView('login'); setTwoFaCode(''); setError(''); }}
                  className="w-full text-sm text-gray-500 hover:text-gray-300 mt-2">
                  {t('backToSignIn')}
                </button>
              </form>
            )}

            {/* === FORGOT PASSWORD === */}
            {view === 'forgot' && (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                {error && <div className="bg-red-900/30 border border-red-700 text-red-300 px-3 py-2 rounded text-sm">{error}</div>}
                {success && <div className="bg-green-900/30 border border-green-700 text-green-300 px-3 py-2 rounded text-sm">{success}</div>}
                <p className="text-gray-400 text-sm">{t('forgotIntro')}</p>
                <div className="space-y-2">
                  <Label className="text-gray-300 text-sm">{t('email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('emailPlaceholder')}
                      className="pl-10 bg-[#1a2236] border-gray-700 text-white placeholder:text-gray-500"
                      required data-testid="forgot-email" />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full text-white"
                  style={{ backgroundColor: brandColor }} data-testid="forgot-submit-btn">
                  {loading ? t('btnSending') : t('btnSendReset')} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <button type="button" onClick={() => { setView('login'); setError(''); setSuccess(''); }}
                  className="w-full text-sm text-gray-500 hover:text-gray-300 mt-2"
                  data-testid="forgot-back-to-login">
                  {t('backToSignIn')}
                </button>
              </form>
            )}

            {/* === RESET PASSWORD === */}
            {view === 'reset' && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                {error && <div className="bg-red-900/30 border border-red-700 text-red-300 px-3 py-2 rounded text-sm">{error}</div>}
                {success && <div className="bg-green-900/30 border border-green-700 text-green-300 px-3 py-2 rounded text-sm">{success}</div>}
                <div className="space-y-2">
                  <Label className="text-gray-300 text-sm">{t('newPassword')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
                    <Input type={showNewPassword ? 'text' : 'password'} value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder={t('newPasswordPlaceholder')}
                      className="pl-10 pr-10 bg-[#1a2236] border-gray-700 text-white placeholder:text-gray-500"
                      required minLength={6} data-testid="reset-new-password" />
                    <button type="button" onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-300">
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full text-white"
                  style={{ backgroundColor: brandColor }} data-testid="reset-submit-btn">
                  {loading ? t('btnResetting') : t('btnReset')} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <div className="mt-6 text-center space-y-1.5">
          <p className="text-gray-500 text-xs" dangerouslySetInnerHTML={{ __html: t('footerLBP') }} />
          <Link to="/admin/login" className="text-gray-500 hover:text-gray-300 text-[11px] transition-colors" data-testid="admin-login-link">
            {t('adminLogin')}
          </Link>
        </div>
      </div>
    </div>
  );
}
